import {
  DocumentSnapshot,
  QueryConstraint,
  QuerySnapshot,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  IGenericError,
  IHomePageTemplate,
  IHomePageTemplateBase,
  IHomePageTemplateRaw,
  HomePageTemplateStatus
} from '../types'

const homePageTemplatesCollectionName = 'homePageTemplates'

const homePageTemplatesCollectionRef = collection(
  firebaseDb,
  homePageTemplatesCollectionName,
)

const homePageTemplatesDocRef = (id: string) =>
  doc(firebaseDb, homePageTemplatesCollectionName, id)

export async function createHomePageTemplate(
  template: IHomePageTemplateBase,
): Promise<string> {
  try {
    const isRouteUnique: boolean = await checkPathIdIsUnique(template)

    if (!isRouteUnique) {
      throw new Error(
        `Template with routeId: "${template.routeId}" already exists`,
      )
    }

    const document = await addDoc(homePageTemplatesCollectionRef, {
      ...template,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })

    return document.id
  } catch (error: unknown) {
    console.error(
      `error while trying to create home-page-template :>> `,
      (error as IGenericError)?.message || error,
      template,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to create home-page-template`,
    )
  }
}

export async function getHomePageTemplateById(
  id: string,
): Promise<IHomePageTemplate | undefined> {
  try {
    const docRef = homePageTemplatesDocRef(id)
    const docSnap = (await getDoc(
      docRef,
    )) as DocumentSnapshot<IHomePageTemplateRaw>

    const dacData = docSnap.data()

    if (!dacData) {
      return undefined
    }

    const doc: IHomePageTemplate = {
      id: docSnap.id,
      ...dacData,
    }

    return doc
  } catch (error: unknown) {
    console.error(
      `error while trying to get home-page-template by id: ${id} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get home-page-template`,
    )
  }
}

export async function getHomePageTemplates(
  queryConstraints: QueryConstraint[] = [],
): Promise<IHomePageTemplate[]> {
  try {
    const q = query(homePageTemplatesCollectionRef, ...queryConstraints)
    const querySnapshot = (await getDocs(
      q,
    )) as unknown as QuerySnapshot<IHomePageTemplateRaw>

    let documents: IHomePageTemplate[] = []

    querySnapshot.forEach((snapshot) => {
      documents.push({
        id: snapshot.id,
        ...snapshot.data(),
      })
    })

    return documents
  } catch (error: unknown) {
    console.error(
      `error while trying to get home-page-template :>> `,
      (error as IGenericError)?.message || error,
      queryConstraints,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get home-page-template`,
    )
  }
}

export async function fetchAllActiveTemplates() {
  const queryConstraints = [where('status', '==', HomePageTemplateStatus.Active)]
  const templates = await getHomePageTemplates(queryConstraints)

  return templates
}

export async function updateHomePageTemplateById(
  templateId: string,
  updateObj: IHomePageTemplateBase,
): Promise<void> {
  try {
    const docRef = homePageTemplatesDocRef(templateId)

    await updateDoc(docRef, { ...updateObj, updatedAt: serverTimestamp() })
  } catch (error: unknown) {
    console.error(
      `error while trying to update home-page-template by id: ${templateId} :>> `,
      (error as IGenericError)?.message || error,
      updateObj,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update home-page-template`,
    )
  }
}

export async function deleteHomePageTemplateById(
  templateId: string,
): Promise<void> {
  try {
    const docRef = homePageTemplatesDocRef(templateId)

    await deleteDoc(docRef)
  } catch (error: unknown) {
    console.error(
      `error while trying to delete home-page-template by id: ${templateId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to delete home-page-template`,
    )
  }
}

async function checkPathIdIsUnique(
  template: IHomePageTemplateBase,
): Promise<boolean> {
  const queryConstraints: QueryConstraint[] = [
    where('routeId', '==', template.routeId),
  ]

  const templates: IHomePageTemplate[] = await getHomePageTemplates(
    queryConstraints,
  )

  return !!templates.length ? false : true
}
